import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { ScreenData, shouldShowCategory } from "../../types/ScreenTypes";
import CachedImage from "../shared/CachedImage";
import VideoPlayerFactory from "../shared/VideoPlayerFactory";
import SimpleUpcomingEventsWidget from "./SimpleUpcomingEventsWidget";
import "./SimpleNoticeboardDisplay.css";
import { YOUTUBE_VIDEO_BUFFER, VIDEO_BUFFER } from "../../constants/general";
import { DigitalAssetType } from "../../types/DigitalAssetTypes";
import LabbaikSlide from "../slideshow/LabbaikSlide";
import useLabbaikItems, { LabbaikItem } from "../../hooks/useLabbaikItems";

interface SimpleNoticeboardItem {
  id: number;
  assetId: number;
  assetTitle: string;
  assetType: string;
  assetUrl: string;
  duration?: number;
  videoType?: string | null;
  isVideoUrl?: boolean;
  videoAutoplay?: boolean;
  videoMuted?: boolean;
  videoLoop?: boolean;
  displayOrder?: number;
  isActive?: boolean;
  eventCategory?: string; // Poster category: Event, FuneralNotice, InformationPoster, etc.
}

interface SimpleNoticeboardDisplayProps {
  items: SimpleNoticeboardItem[];
  screenData: ScreenData;
  width: number;
  height: number;
  onComplete?: () => void;
  masjidId?: number;
}

interface PosterWithDimensions {
  id: string;
  url: string;
  title: string;
  aspectRatio: number;
  labbaik?: LabbaikItem;
}

interface LayoutCell {
  id: string;
  url: string;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number;
  aspectRatio: number;
  labbaik?: LabbaikItem;
}

interface DisplayableItem {
  type: "posterPage" | "video" | "upcomingEventsWidget";
  pageIndex?: number;
  videoItem?: SimpleNoticeboardItem;
  widgetItem?: SimpleNoticeboardItem;
}

/**
 * SimpleNoticeboardDisplay - Simplified noticeboard for playlist display
 * Only shows posters in a grid layout - no prayer times, no footer, no complexity
 */
function SimpleNoticeboardDisplay({
  items,
  screenData,
  width,
  height,
  onComplete,
  masjidId,
}: SimpleNoticeboardDisplayProps) {
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [postersWithDimensions, setPostersWithDimensions] = useState<
    PosterWithDimensions[]
  >([]);
  const [isLoadingDimensions, setIsLoadingDimensions] = useState(true);
  const [elapsedTime, setElapsedTime] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Separate videos from posters and special widgets
  const { posterItems, videoItems, widgetItems } = useMemo(() => {
    const posters: SimpleNoticeboardItem[] = [];
    const videos: SimpleNoticeboardItem[] = [];
    const widgets: SimpleNoticeboardItem[] = [];

    items.forEach((item) => {
      // Skip inactive items
      if (item.isActive === false) {
        return;
      }

      // Filter by poster category (showEventPosters, showFuneralNotices, showInformationPosters)
      const category = item.eventCategory || "Event";
      if (!shouldShowCategory(screenData, category)) {
        console.log(
          `[SimpleNoticeboard] Filtering out item "${item.assetTitle}" with category "${category}"`,
        );
        return;
      }

      // Check if item has valid URL (for assets)
      if (item.assetType === "upcomingEventsWidget") {
        widgets.push(item);
      } else if (item.assetType.toLowerCase() === "video") {
        videos.push(item);
      } else {
        posters.push(item);
      }
    });

    return { posterItems: posters, videoItems: videos, widgetItems: widgets };
  }, [items, screenData]);

  // Get noticeboard settings with defaults
  const settings = screenData.noticeboardSettings || {};

  const columns = (settings as any).columns ?? 2;
  const maxRowsPerPage = (settings as any).maxRowsPerPage ?? 2;
  const padding = (settings as any).padding ?? 20;
  const pageTransitionDuration = (settings as any).pageTransitionDuration ?? 30;
  const backgroundColor = (settings as any).backgroundColor ?? "#1a1a1a";
  const showBorders = (settings as any).showBorders ?? true;
  const borderColor = (settings as any).borderColor ?? "#333333";
  const borderWidth = (settings as any).borderWidth ?? 2;

  // Load poster dimensions
  useEffect(() => {
    if (posterItems.length === 0) {
      setIsLoadingDimensions(false);
      return;
    }

    const loadDimensions = async () => {
      const dimensionPromises = posterItems.map((item) => {
        return new Promise<PosterWithDimensions>((resolve) => {
          const fallback: PosterWithDimensions = {
            id: String(item.id),
            url: item.assetUrl,
            title: item.assetTitle,
            aspectRatio: 16 / 9,
          };
          const img = new Image();
          // A stalled request must never block the whole noticeboard render
          const timer = window.setTimeout(() => resolve(fallback), 10000);
          img.onload = () => {
            clearTimeout(timer);
            resolve({ ...fallback, aspectRatio: img.width / img.height });
          };
          img.onerror = () => {
            clearTimeout(timer);
            resolve(fallback);
          };
          if (item.assetUrl?.includes("/digital-signage/")) {
            img.crossOrigin = "anonymous";
          }
          img.src = item.assetUrl;
        });
      });

      const loadedPosters = await Promise.all(dimensionPromises);
      setPostersWithDimensions(loadedPosters);
      setIsLoadingDimensions(false);
    };

    loadDimensions();
  }, [posterItems]);

  // Labbaik items join the grid after the playlist posters as poster-shaped cards
  const labbaikItems = useLabbaikItems(
    screenData.masjidId ?? masjidId,
    screenData.showLabbaik,
  );
  const gridPosters = useMemo<PosterWithDimensions[]>(
    () => [
      ...postersWithDimensions,
      ...labbaikItems.map((item, index) => ({
        id: `labbaik-${index}`,
        url: "",
        title: item.title || "",
        aspectRatio: 2 / 3,
        labbaik: item,
      })),
    ],
    [postersWithDimensions, labbaikItems],
  );

  // Calculate layout pages
  const layoutPages = useMemo(() => {
    if (gridPosters.length === 0) return [];

    const itemsPerPage = columns * maxRowsPerPage;
    const pages: LayoutCell[][] = [];

    for (let i = 0; i < gridPosters.length; i += itemsPerPage) {
      const pagePosters = gridPosters.slice(i, i + itemsPerPage);
      const cells: LayoutCell[] = [];

      const cellWidth = (width - padding * (columns + 1)) / columns;
      const cellHeight =
        (height - padding * (maxRowsPerPage + 1)) / maxRowsPerPage;

      pagePosters.forEach((poster, index) => {
        const row = Math.floor(index / columns);
        const col = index % columns;

        const x = padding + col * (cellWidth + padding);
        const y = padding + row * (cellHeight + padding);

        cells.push({
          id: poster.id,
          url: poster.url,
          title: poster.title,
          x,
          y,
          width: cellWidth,
          height: cellHeight,
          aspectRatio: poster.aspectRatio,
          labbaik: poster.labbaik,
        });
      });

      pages.push(cells);
    }

    return pages;
  }, [gridPosters, width, height, columns, maxRowsPerPage, padding]);

  // Track stable item references and snapshot logic
  const itemsRef = useRef<DisplayableItem[]>([]);
  const cycleActiveRef = useRef(false);
  const displayableItemsSnapshotRef = useRef<DisplayableItem[]>([]);

  // Create displayable items (poster pages + videos)
  const displayableItems = useMemo(() => {
    // Only use snapshot during active cycling to prevent flickering,
    // but always rebuild when layoutPages has content (indicating new data is ready)
    const shouldUseSnapshot =
      cycleActiveRef.current &&
      displayableItemsSnapshotRef.current.length > 0 &&
      layoutPages.length === 0; // Only snapshot when layoutPages is still empty

    if (shouldUseSnapshot) {
      return displayableItemsSnapshotRef.current;
    }

    const items: DisplayableItem[] = [];

    // Add poster pages
    layoutPages.forEach((_, index) => {
      items.push({
        type: "posterPage",
        pageIndex: index,
      });
    });

    // Add videos
    videoItems.forEach((video) => {
      items.push({
        type: "video",
        videoItem: video,
      });
    });

    // Add special widgets
    widgetItems.forEach((widget) => {
      items.push({
        type: "upcomingEventsWidget",
        widgetItem: widget,
      });
    });

    // Only update ref if actual content changed
    if (JSON.stringify(items) !== JSON.stringify(itemsRef.current)) {
      itemsRef.current = items;
    }

    // Update snapshot if cycle is active and we have items
    if (items.length > 0) {
      displayableItemsSnapshotRef.current = items;
      cycleActiveRef.current = true;
    }

    return itemsRef.current;
  }, [layoutPages, videoItems, widgetItems]);

  // Reset currentItemIndex when displayableItems array changes
  useEffect(() => {
    // If current index is out of bounds, reset to 0
    if (currentItemIndex >= displayableItems.length) {
      setCurrentItemIndex(0);
    }
  }, [displayableItems.length]); // Only depend on length to avoid excessive resets

  // Progress bar timer - updates elapsed time every second
  useEffect(() => {
    if (displayableItems.length === 0 || isLoadingDimensions) return;

    // Reset elapsed time when item changes
    setElapsedTime(0);

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Start new interval to update elapsed time
    intervalRef.current = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [currentItemIndex, displayableItems.length, isLoadingDimensions]);

  // Prevent transitions during an ongoing transition
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Memoize the completion handler to maintain stable reference
  const handleItemComplete = useCallback(() => {
    // handleItemComplete called
    // Prevent multiple triggers
    if (isTransitioning) {
      return;
    }

    setIsTransitioning(true);

    // Small delay to ensure cleanup happens first
    setTimeout(() => {
      if (currentItemIndex >= displayableItems.length - 1) {
        // Finished the cycle
        if (onComplete) {
          onComplete();
        }
        setCurrentItemIndex(0); // Reset for next time
      } else {
        // Advance to next item
        setCurrentItemIndex((prev) => prev + 1);
      }

      // Reset transition flag after a short delay
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }, 100);
  }, [currentItemIndex, displayableItems.length, onComplete, isTransitioning]);

  // Page rotation timer - stabilized with refs
  useEffect(() => {
    if (displayableItems.length === 0 || isLoadingDimensions) return;

    // Timer useEffect starting

    // Track whether this effect instance is still current
    const isCurrentEffect = { current: true };

    // Clear existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const currentItem = displayableItems[currentItemIndex];

    // Safety check: if currentItem is undefined, don't proceed
    if (!currentItem) {
      return;
    }

    // For videos, use a more reliable approach with unique ID
    if (currentItem.type === "video" && currentItem.videoItem) {
      const videoItem = currentItem.videoItem;

      // Get a stable ID for the video to track it
      const videoId = videoItem.id || videoItem.assetId;
      const videoUrl = videoItem.assetUrl;

      // Determine video type and set appropriate buffer
      const isYouTube =
        videoUrl?.includes("youtube") || videoUrl?.includes("youtu.be");
      const duration = videoItem.duration || 30; // Default 30s
      const bufferTime = isYouTube ? YOUTUBE_VIDEO_BUFFER : VIDEO_BUFFER; // Add extra buffer for YouTube videos
      const totalDuration = duration + bufferTime;

      // Set timer with cleanup safety
      timerRef.current = setTimeout(() => {
        // Only proceed if this effect is still current
        if (isCurrentEffect.current) {
          // timer fired for video
          handleItemComplete();
        }
      }, totalDuration * 1000);

      return () => {
        isCurrentEffect.current = false;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
      };
    }

    // For poster pages, use the configured duration
    timerRef.current = setTimeout(() => {
      // Only proceed if this effect is still current
      if (isCurrentEffect.current) {
        // timer fired for poster page
        handleItemComplete();
      }
    }, pageTransitionDuration * 1000);

    return () => {
      isCurrentEffect.current = false;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [currentItemIndex, pageTransitionDuration, handleItemComplete]);

  if (isLoadingDimensions) {
    return (
      <div className="simple-noticeboard-loading" style={{ width, height }}>
        <div className="loading-message">Loading posters...</div>
      </div>
    );
  }

  if (displayableItems.length === 0) {
    return (
      <div className="simple-noticeboard-empty" style={{ width, height }}>
        <div className="empty-message">No content to display</div>
      </div>
    );
  }

  const currentItem = displayableItems[currentItemIndex];

  // Calculate total duration for current item
  const getCurrentItemDuration = () => {
    if (!currentItem) return pageTransitionDuration;

    if (currentItem.type === "video" && currentItem.videoItem) {
      const videoItem = currentItem.videoItem;
      const isYouTube =
        videoItem.assetUrl?.includes("youtube") ||
        videoItem.assetUrl?.includes("youtu.be");
      const duration = videoItem.duration || 30;
      const bufferTime = isYouTube ? YOUTUBE_VIDEO_BUFFER : VIDEO_BUFFER;
      return duration + bufferTime;
    }

    if (currentItem.type === "upcomingEventsWidget" && currentItem.widgetItem) {
      return currentItem.widgetItem.duration || 15;
    }

    return pageTransitionDuration;
  };

  const currentItemDuration = getCurrentItemDuration();
  const progress = Math.min((elapsedTime / currentItemDuration) * 100, 100);
  const noticeboardPosterFitMode =
    screenData.noticeboardSettings?.noticeboardPosterFitMode ?? "auto";

  // Custom CSS variables for theming
  const customStyles = {
    "--compact-primary-color": screenData.primaryColor || "#FFC107",
  } as React.CSSProperties;

  // Render video with a stable key pattern
  if (currentItem.type === "video" && currentItem.videoItem) {
    const videoItem = currentItem.videoItem;

    // Generate a stable key that won't change during re-renders
    const videoId = videoItem.id || videoItem.assetId;
    const videoKey = `video-${videoId}-${currentItemIndex}`;

    return (
      <div
        className="simple-noticeboard-video-fullscreen"
        style={{
          width,
          height,
          position: "relative",
          overflow: "hidden",
          ...customStyles,
        }}
      >
        <VideoPlayerFactory
          key={videoKey}
          item={{
            ...videoItem,
            // Explicitly set all required properties to avoid undefined values
            id: videoItem.id || videoItem.assetId,
            assetId: videoItem.assetId || videoItem.id,
            assetUrl: videoItem.assetUrl,
            assetTitle: videoItem.assetTitle || "Untitled Video",
            assetType: "video",
            duration: videoItem.duration || 30,
            videoAutoplay: videoItem.videoAutoplay !== false,
            videoMuted: videoItem.videoMuted ?? true,
            videoLoop: videoItem.videoLoop === true,
          }}
          onEnded={() => {
            setTimeout(handleItemComplete, 100);
          }}
          onError={(error) => {
            setTimeout(handleItemComplete, 100);
          }}
          className="simple-noticeboard-video-player"
          fillMode="contain"
          screenData={{
            showPortraitPosterOnLandscape:
              screenData.showPortraitPosterOnLandscape,
            showLandscapePosterOnPortrait:
              screenData.showLandscapePosterOnPortrait,
            orientation: screenData.orientation,
          }}
        />

        {/* Progress Bar */}
        <div className="simple-noticeboard-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Page indicator */}
        {displayableItems.length > 1 && (
          <div className="simple-noticeboard-page-indicator">
            {currentItemIndex + 1} / {displayableItems.length} (Video)
          </div>
        )}
      </div>
    );
  }

  // Render upcoming events widget
  if (
    currentItem.type === "upcomingEventsWidget" &&
    currentItem.widgetItem &&
    masjidId
  ) {
    return (
      <div
        className="simple-noticeboard-upcoming-events"
        style={{
          width,
          height,
          position: "relative",
          overflow: "hidden",
          ...customStyles,
        }}
      >
        <SimpleUpcomingEventsWidget
          masjidId={masjidId}
          duration={currentItem.widgetItem.duration || 15}
          onComplete={() => {
            setTimeout(handleItemComplete, 100);
          }}
          screenData={screenData}
          primaryColor={screenData.primaryColor}
          secondaryColor={screenData.secondaryColor}
          accentColor={screenData.accentColor}
          width={width}
          height={height}
          restartTrigger={currentItemIndex} // Use currentItemIndex as restart trigger
        />

        {/* Progress Bar */}
        <div className="simple-noticeboard-progress">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        {/* Page indicator */}
        {displayableItems.length > 1 && (
          <div className="simple-noticeboard-page-indicator">
            {currentItemIndex + 1} / {displayableItems.length} (Events)
          </div>
        )}
      </div>
    );
  }

  // Render poster page
  const currentPage = layoutPages[currentItem.pageIndex!];

  return (
    <div
      className="simple-noticeboard-display"
      style={{
        width,
        height,
        ...customStyles,
      }}
    >
      {/* Poster Grid */}
      {currentPage.map((cell) => {
        let fitMode: "contain" | "fill" | "cover";
        if (noticeboardPosterFitMode !== "auto") {
          fitMode = noticeboardPosterFitMode as "contain" | "fill" | "cover";
        } else {
          // Orientation-aware fitting fallback:
          // Same orientation → fill (slight stretch, no bars, no cropping)
          // Different orientation → contain (bars accepted)
          const cellAspect = cell.width / cell.height;
          const imageIsLandscape = cell.aspectRatio > 1;
          const cellIsLandscape = cellAspect > 1;
          const orientationsMatch =
            (imageIsLandscape && cellIsLandscape) ||
            (!imageIsLandscape && !cellIsLandscape);
          fitMode = orientationsMatch ? "fill" : "contain";
        }

        return (
          <div
            key={cell.id}
            className="simple-noticeboard-cell"
            style={{
              position: "absolute",
              left: cell.x,
              top: cell.y,
              width: cell.width,
              height: cell.height,
              overflow: "hidden",
            }}
          >
            {cell.labbaik ? (
              <LabbaikSlide item={cell.labbaik} colours={screenData} />
            ) : (
              <CachedImage
                src={cell.url}
                alt={cell.title}
                className="simple-noticeboard-image"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: fitMode,
                }}
              />
            )}
          </div>
        );
      })}

      {/* Progress Bar */}
      <div className="simple-noticeboard-progress">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Page indicator (only if multiple items) */}
      {displayableItems.length > 1 && (
        <div className="simple-noticeboard-page-indicator">
          {currentItemIndex + 1} / {displayableItems.length}
        </div>
      )}
    </div>
  );
}

// Export a memoized version to prevent unnecessary re-renders
export default React.memo(SimpleNoticeboardDisplay, (prevProps, nextProps) => {
  // Avoid re-rendering on unrelated `screenData` changes.
  // Compare items by stable identity (length + ids) rather than reference.
  const prevItems = prevProps.items || [];
  const nextItems = nextProps.items || [];

  let itemsEqual = false;
  if (prevItems === nextItems) {
    itemsEqual = true;
  } else if (prevItems.length === nextItems.length) {
    itemsEqual = prevItems.every((it, idx) => {
      const other = nextItems[idx];
      return it?.id === other?.id && it?.assetId === other?.assetId;
    });
  }

  // Only consider noticeboard-relevant parts of screenData
  const prevSettings = prevProps.screenData?.noticeboardSettings || {};
  const nextSettings = nextProps.screenData?.noticeboardSettings || {};
  const settingsEqual =
    JSON.stringify(prevSettings) === JSON.stringify(nextSettings);

  const colorsEqual =
    prevProps.screenData?.primaryColor === nextProps.screenData?.primaryColor &&
    prevProps.screenData?.secondaryColor ===
      nextProps.screenData?.secondaryColor;

  return (
    itemsEqual &&
    settingsEqual &&
    colorsEqual &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.onComplete === nextProps.onComplete
  );
});
